// src/pages/MachineLearningPage.tsx
// "Learn machine learning" landing page at /machine-learning. Hook: ₹249/month,
// cancel anytime — with a FREE live ML webinar (Fri Oct 16) for subscribers.
// Light cream theme matching WebinarAugPage/HomePage.
//
// Two distinct actions:
//   1) MONEY — subscribe ₹249/mo. Happens INSIDE the app: CTA → /login?next=/pay
//      (sign in → PostLoginRedirect honors ?next → /pay paywall). No Razorpay here.
//   2) WEBINAR SEAT — the on-page form captures name/email/WhatsApp (saved via
//      POST /api/webinar-register, slug `machine-learning`, UTMs + Lead pixel),
//      then reveals the WhatsApp group link.
//
// Differentiating webinar subscribers from regular payers: the WhatsApp group is
// gated to subscribers, so being in the group marks a paying webinar customer —
// and as an explicit backup we ask them to email support@cubemate.ai with the
// subject "Webinar access needed" after paying.
import React, { useEffect, useState } from 'react';
import { ML } from './machineLearningConfig';

const WHY: Array<[string, string]> = [
  ['🧠 Learn ML by watching it run', 'Not slideware. Models train live in your browser — you see the data, the fit, the metrics move, and understand why, not just what.'],
  ['🪜 One step at a time', 'A guided path from "what is a feature?" to training and evaluating real models. Each step builds on the last — no prior ML needed.'],
  ['🛠️ Build things you keep', 'Finish with real notebooks and small ML projects you actually understand — the kind you can put on a resume and explain.'],
];

const WEBINAR_AGENDA: Array<[string, string]> = [
  ['What ML actually is', 'The honest mental model — what machine learning can and can\'t do, minus the hype.'],
  ['A model, trained live', 'We take real data and train an ML model from scratch on screen, start to finish.'],
  ['Your path from here', 'The exact order to learn ML on Cubemate, and how to keep building after the session. Open Q&A.'],
];

const MachineLearningPage: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', whatsapp: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Track UTM-tagged ad visits — same pattern as /webinar-aug and /build-india
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = params.get('utm_source') || params.get('gclid');
    if (!hasUtm) return;
    const apiUrl = process.env.REACT_APP_API_URL || '';
    fetch(`${apiUrl}/api/ad-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        landing_page: '/machine-learning',
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_content: params.get('utm_content') || undefined,
        utm_term: params.get('utm_term') || undefined,
        gclid: params.get('gclid') || undefined,
        referrer: document.referrer || undefined,
      }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.visit_id) localStorage.setItem('ad_visit_id', String(data.visit_id));
      })
      .catch(() => { /* best-effort */ });
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');
    const params = new URLSearchParams(window.location.search);
    const apiUrl = process.env.REACT_APP_API_URL || '';
    try {
      const res = await fetch(`${apiUrl}/api/webinar-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          webinar_slug: ML.slug,
          name: form.name,
          email: form.email,
          whatsapp: form.whatsapp,
          segment: 'ml_webinar',
          utm_source: params.get('utm_source') || undefined,
          utm_medium: params.get('utm_medium') || undefined,
          utm_campaign: params.get('utm_campaign') || undefined,
          utm_content: params.get('utm_content') || undefined,
          utm_term: params.get('utm_term') || undefined,
          referrer: document.referrer || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || 'Registration failed. Please try again.');
      }
      localStorage.setItem('ml_reg_id', String(data.registration_id));
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'ml_webinar_registration' });
        // Meta campaigns count "Website Completed Registration" — fire alongside Lead
        window.fbq('track', 'CompleteRegistration', { content_name: 'ml_webinar_registration' });
      }
      if (ML.googleAdsConversion && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { send_to: ML.googleAdsConversion });
      }
      setStatus('done');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const priceLabel = `${ML.priceDisplay}${ML.priceCadence}`;
  const mailto = `mailto:${ML.supportEmail}?subject=${encodeURIComponent(ML.supportSubject)}`;

  return (
    <div className="ml-root">
      <style>{`
        .ml-root {
          --bg: #fefae8;
          --bg-2: #ffffff;
          --bg-3: #fff5d1;
          --line: #1a1a2e15;
          --ink: #1a1a2e;
          --ink-soft: #40405a;
          --ink-mute: #6b6b85;
          --teal: #0d9488;
          --violet: #7c3aed;
          --p-gradient: linear-gradient(135deg, #14b8a6 0%, #8b5cf6 100%);
          font-family: 'Inter', system-ui, sans-serif;
          background: var(--bg);
          color: var(--ink);
          -webkit-font-smoothing: antialiased;
          min-height: 100vh;
          color-scheme: light;
        }
        .ml-root .mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .ml-root .m-grad-text {
          background: var(--p-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .ml-root .m-grad-bg { background: var(--p-gradient); }
        .ml-root .m-card {
          background: var(--bg-2);
          border: 1.5px solid var(--line);
          border-radius: 18px;
          box-shadow: 0 1px 0 rgba(26,26,46,.04), 0 8px 24px -10px rgba(26,26,46,.08);
        }
        .ml-root .m-input {
          width: 100%;
          background: #ffffff;
          border: 1.5px solid #1a1a2e26;
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 16px; /* >=16px prevents iOS Safari auto-zoom on focus */
          color: var(--ink);
          outline: none;
          transition: border-color .2s;
        }
        .ml-root .m-input:focus { border-color: #14b8a6; }
        .ml-root .m-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 6px;
          color: var(--ink-soft);
        }
        @keyframes m-spin { to { transform: rotate(360deg); } }
        .ml-root .m-spin { animation: m-spin 1s linear infinite; display: inline-block; }
      `}</style>

      {/* ───── NAV ───── */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b" style={{ background: 'rgba(254,250,232,0.85)', borderColor: '#1a1a2e14' }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/cubemate-logo.png" alt="Cubemate" className="w-8 h-8 rounded-lg" />
            <span className="font-bold tracking-tight text-lg">Cubemate</span>
          </a>
          <a href={ML.payUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 text-sm font-semibold rounded-full m-grad-bg text-white hover:opacity-90 transition shadow-md">
            Start learning — {priceLabel} →
          </a>
        </div>
      </nav>

      {/* ───── HERO ───── */}
      <header className="pt-28 md:pt-32 pb-10 px-5 md:px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs mono uppercase tracking-widest font-semibold mb-4" style={{ color: 'var(--teal)' }}>
            🔴 free live ml webinar · {ML.webinarDateDisplay}
          </p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-[1.08] mb-5">
            Learn machine learning. <span className="m-grad-text">{ML.priceDisplay} a month — cancel anytime.</span>
          </h1>
          <p className="text-lg leading-relaxed mb-6" style={{ color: 'var(--ink-soft)' }}>
            Start ML on Cubemate for just {priceLabel} — models train live in your browser, one guided step at a
            time. Subscribe now and your seat for our <strong>free live ML webinar on {ML.webinarDateDisplay.split(' · ')[0]}</strong> is included.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
            <span className="px-4 py-2 rounded-full text-sm font-bold border bg-white" style={{ borderColor: '#14b8a655', color: 'var(--teal)' }}>
              💳 {priceLabel} · cancel anytime
            </span>
            <span className="px-4 py-2 rounded-full text-sm font-bold text-white m-grad-bg">
              🎓 Webinar free for subscribers
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href={ML.payUrl} target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-3.5 font-bold rounded-full m-grad-bg text-white hover:opacity-90 transition shadow-lg">
              Start learning — {priceLabel} →
            </a>
            <a href="#register" className="inline-block px-8 py-3.5 font-bold rounded-full border-2 bg-white hover:bg-gray-50 transition" style={{ borderColor: '#7c3aed55', color: 'var(--violet)' }}>
              Reserve my webinar seat
            </a>
          </div>
        </div>
      </header>

      {/* ───── WHY LEARN ML ON CUBEMATE ───── */}
      <section className="py-12 px-5 md:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-center mb-8">Why learn ML on Cubemate</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {WHY.map(([title, desc]) => (
              <div key={title} className="m-card px-5 py-5">
                <p className="font-bold mb-1">{title}</p>
                <p className="text-base" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── THE FREE WEBINAR ───── */}
      <section className="py-12 px-5 md:px-6" style={{ background: 'linear-gradient(180deg, transparent, #fff5d180, transparent)' }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-center mb-3">Your free live ML webinar</h2>
          <p className="text-center text-base mb-8 max-w-2xl mx-auto" style={{ color: 'var(--ink-soft)' }}>
            {ML.webinarDateDisplay} — free for every {ML.priceDisplay}/month subscriber. A real model, trained live,
            with your questions answered.
          </p>
          <div className="space-y-4">
            {WEBINAR_AGENDA.map(([title, desc]) => (
              <div key={title} className="m-card px-5 md:px-6 py-5">
                <p className="font-bold mb-1">{title}</p>
                <p className="text-base" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section className="py-12 px-5 md:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-center mb-8">How to join the webinar</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              ['1', `Subscribe ${priceLabel}`, 'Sign in to Cubemate and start your subscription — it all happens inside the app. Cancel anytime.'],
              ['2', `Join the WhatsApp group by ${ML.groupJoinBy}`, 'Register below to get the group link. The group is for subscribers only — it\'s where the joining link and reminders go.'],
              ['3', `Show up live`, `Join from your browser on ${ML.webinarDateDisplay.split(' · ')[0]} — nothing to install.`],
            ].map(([n, title, desc]) => (
              <div key={n} className="m-card p-6 text-center">
                <div className="m-grad-bg text-white w-10 h-10 rounded-xl grid place-items-center text-lg font-extrabold mx-auto mb-3">{n}</div>
                <p className="font-bold mb-1">{title}</p>
                <p className="text-base" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── PRICING ───── */}
      <section className="py-12 px-5 md:px-6" style={{ background: 'linear-gradient(180deg, transparent, #fff5d180, transparent)' }}>
        <div className="max-w-md mx-auto">
          <div className="m-card p-8 text-center">
            <p className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--teal)' }}>Machine Learning</p>
            <p className="text-5xl font-black mb-1">
              {ML.priceDisplay}<span className="text-xl font-semibold" style={{ color: 'var(--ink-mute)' }}>{ML.priceCadence}</span>
            </p>
            <p className="text-sm mb-6" style={{ color: 'var(--ink-mute)' }}>Cancel anytime</p>
            <ul className="text-left space-y-3 mb-8">
              {[
                'Full ML learning path — models train live in your browser',
                'Free live ML webinar seat (' + ML.webinarDateDisplay.split(' · ')[0] + ')',
                'Keep the notebooks and projects you build',
                'Cancel anytime — no lock-in',
              ].map(f => (
                <li key={f} className="flex items-start gap-3">
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="#7c3aed" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-base" style={{ color: 'var(--ink-soft)' }}>{f}</span>
                </li>
              ))}
            </ul>
            <a href={ML.payUrl} target="_blank" rel="noopener noreferrer" className="block w-full py-3.5 font-bold rounded-full m-grad-bg text-white hover:opacity-90 transition shadow-lg">
              Start learning — {priceLabel} →
            </a>
            <p className="text-xs mt-3" style={{ color: 'var(--ink-mute)' }}>Secure payment inside the Cubemate app.</p>
          </div>
        </div>
      </section>

      {/* ───── REGISTRATION (webinar seat / WhatsApp group) ───── */}
      <section className="py-14 px-5 md:px-6">
        <div id="register" className="m-card p-6 md:p-8 max-w-md mx-auto scroll-mt-24">
          {status === 'done' ? (
            <div className="text-center">
              <p className="text-2xl font-black tracking-tight mb-2">🎉 You're on the list, {form.name.split(' ')[0] || 'there'}!</p>
              <p className="text-base mb-6" style={{ color: 'var(--ink-soft)' }}>
                Two quick things to lock in your free webinar seat:
              </p>
              <ol className="text-left space-y-3 mb-6">
                <li className="flex gap-3">
                  <span className="m-grad-bg text-white w-6 h-6 rounded-lg grid place-items-center text-sm font-bold shrink-0">1</span>
                  <span className="text-base" style={{ color: 'var(--ink-soft)' }}>
                    Subscribe for {priceLabel} (cancel anytime) — the webinar is free for subscribers.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="m-grad-bg text-white w-6 h-6 rounded-lg grid place-items-center text-sm font-bold shrink-0">2</span>
                  <span className="text-base" style={{ color: 'var(--ink-soft)' }}>
                    Join the subscribers-only WhatsApp group by {ML.groupJoinBy} for the joining link.
                  </span>
                </li>
              </ol>
              <a href={ML.payUrl} target="_blank" rel="noopener noreferrer" className="block w-full py-3.5 font-bold rounded-full m-grad-bg text-white hover:opacity-90 transition shadow-lg mb-3">
                Subscribe now — {priceLabel} →
              </a>
              {ML.whatsappLink ? (
                <a href={ML.whatsappLink} target="_blank" rel="noopener noreferrer"
                  className="block w-full py-3.5 font-bold rounded-full text-white transition shadow-lg" style={{ background: '#22c55e' }}>
                  💬 Join the WhatsApp group
                </a>
              ) : (
                <p className="text-sm" style={{ color: 'var(--ink-mute)' }}>
                  We'll send the WhatsApp group link to your email and number shortly.
                </p>
              )}
              <p className="text-xs mt-4" style={{ color: 'var(--ink-mute)' }}>
                Already paid? Email <a href={mailto} className="font-semibold" style={{ color: 'var(--violet)' }}>{ML.supportEmail}</a> (subject "{ML.supportSubject}") and we'll confirm your access.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-black tracking-tight mb-1">Reserve your free webinar seat</h2>
              <p className="text-base mb-6" style={{ color: 'var(--ink-soft)' }}>
                {ML.webinarDateDisplay} · free for {ML.priceDisplay}/month subscribers. Register to get the WhatsApp group link.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="m-label" htmlFor="ml-name">Full name</label>
                  <input id="ml-name" className="m-input" required maxLength={200} placeholder="Your name"
                    value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="m-label" htmlFor="ml-email">Email</label>
                  <input id="ml-email" className="m-input" required type="email" maxLength={255} placeholder="you@example.com"
                    value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <label className="m-label" htmlFor="ml-whatsapp">WhatsApp number</label>
                  <input id="ml-whatsapp" className="m-input" required type="tel" minLength={10} maxLength={20} placeholder="+91 98765 43210"
                    value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} />
                </div>
                {status === 'error' && (
                  <p className="text-sm font-semibold rounded-lg px-3 py-2" style={{ background: '#fee2e2', color: '#b91c1c' }}>{errorMsg}</p>
                )}
                <button type="submit" disabled={status === 'submitting'}
                  className="w-full py-3.5 font-bold rounded-full m-grad-bg text-white hover:opacity-90 transition shadow-lg disabled:opacity-70">
                  {status === 'submitting'
                    ? <span><span className="m-spin">◐</span> Registering…</span>
                    : 'Reserve my seat'}
                </button>
                <p className="text-xs text-center" style={{ color: 'var(--ink-mute)' }}>
                  Payment happens inside the Cubemate app. No spam — only webinar updates.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section className="py-12 px-5 md:px-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {[
            ['Do I need any ML or maths background?', 'No. The path starts from the basics and models train live in front of you, so you learn by seeing, not by wading through theory first. Being comfortable writing a little code helps.'],
            ['How much does it cost?', `${priceLabel}, and you can cancel anytime. The live ML webinar on ${ML.webinarDateDisplay.split(' · ')[0]} is free for subscribers.`],
            ['Where do I pay?', 'Inside the Cubemate app. Click any "Start learning" button, sign in, and start your subscription on the payment page.'],
            ['Is the webinar really free?', `Yes — it's included free with your ${ML.priceDisplay}/month subscription. Subscribe, join the subscribers-only WhatsApp group by ${ML.groupJoinBy}, and show up live.`],
            ['I paid — how do you know to give me webinar access?', `Join the subscribers-only WhatsApp group after you pay — that's how we identify webinar attendees. As a backup, email ${ML.supportEmail} with the subject "${ML.supportSubject}" and we'll confirm.`],
          ].map(([q, a]) => (
            <details key={q} className="m-card px-6 py-4 group">
              <summary className="font-bold cursor-pointer list-none flex justify-between items-center">
                {q}
                <span className="transition group-open:rotate-45 text-lg" style={{ color: 'var(--teal)' }}>+</span>
              </summary>
              <p className="mt-3 text-base leading-relaxed" style={{ color: 'var(--ink-soft)' }}>{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ───── MOBILE STICKY CTA (hidden on desktop) ───── */}
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden border-t backdrop-blur-md px-5 py-3 flex items-center justify-between gap-3"
        style={{ background: 'rgba(254,250,232,0.94)', borderColor: '#1a1a2e14' }}>
        <div className="min-w-0">
          <div className="font-black text-sm">{priceLabel} · cancel anytime</div>
          <div className="text-xs truncate" style={{ color: 'var(--ink-mute)' }}>🎓 Free webinar {ML.webinarDateDisplay.split(' · ')[0]}</div>
        </div>
        <a href={ML.payUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 px-5 py-2.5 rounded-full m-grad-bg text-white font-bold text-sm shadow-md">
          Start learning →
        </a>
      </div>

      {/* ───── FOOTER ───── */}
      <footer className="border-t pt-10 pb-24 lg:pb-10 px-6" style={{ borderColor: '#1a1a2e14' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm" style={{ color: 'var(--ink-mute)' }}>
          <div className="flex items-center gap-2.5">
            <img src="/cubemate-logo.png" alt="Cubemate" className="w-7 h-7 rounded-lg" />
            <span className="font-semibold" style={{ color: 'var(--ink)' }}>Cubemate</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/privacy" className="hover:text-black transition">Privacy</a>
            <a href="/terms" className="hover:text-black transition">Terms</a>
          </div>
        </div>
        <p className="text-center text-xs mt-6" style={{ color: 'var(--ink-mute)' }}>© 2026 Cubemate. Made in India.</p>
      </footer>
    </div>
  );
};

export default MachineLearningPage;
